import React, { useState } from "react";
import { StandardPageWrapper } from "../../../../components/layout/StandardPageWrapper";
import "./us-states-hex-grid.scss";
import { Button, Modal, Breadcrumbs, ToolTip, useSnackBar } from "@truevault/aquilo";
import {
  ProgressFooterSaveNotification,
  useEditPrivacyCenter,
  useUpdateNoticeApi,
} from "../../../../components/privacyCenter/EditPrivacyCenter";
import { usePrivacyCenterNotice } from "../../../../hooks/usePrivacyCenterNotice";
import { USStateNoticesEditor } from "./USStateNoticesEditors";
import { PrivacyCenterTabsNew } from "../PrivacyCenterTabsNew";
import { ProgressFooter, ProgressFooterActions } from "../../../get-compliant/ProgressFooter";
import {
  EyeIcon,
  PrinterIcon,
  RocketLaunchIcon,
  DocumentTextIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/solid";
import { Redirect, useHistory } from "react-router";
import { ApiRouteHelpers } from "../../../../../common/root/apiRoutes";
import { useDataRetentionModule } from "../../../../components/organisms/data-retention/DataRetentionModule";
import { usePrivacyCenterPreviewPopup } from "../../StayCompliantPrivacyCenterPage";
import moment from "moment";
import {
  PrivacyCenterPublishDatePickerModal,
  usePrivacyCenterPublish,
} from "../../../../components/privacyCenter/PrivacyCenterPublish";
import { useSurvey } from "../../../../../common/hooks/api/useSurvey";
import {
  CONSUMER_INCENTIVES_SURVEY_NAME,
  OFFERS_CONSUMER_INCENTIVES_SLUG,
} from "../../../../surveys/consumerCollectionIncentivesSurvey";
import { PageLoading } from "../../../../components";
import { DEVELOPER_INSTRUCTIONS_SURVEY_NAME } from "../../PrivacyNoticeChecklistItems";
import { Routes } from "../../../../root/routes";
import { useLocalStorage } from "../../../../hooks/useLocalStorage";
import { PrivacyCenterDto } from "../../../../../common/service/server/dto/PrivacyCenterDto";
import { didFinishPoliciesAndNoticesOnboarding } from "../../../../../common/hooks/useOnboardingProgress";

export type StateNoticesStep =
  | "statesNoticesEditor"
  | "financialIncentivesEditor"
  | "biometricIdentifiersEditor";

export function USStateNoticesPage() {
  const editPrivacyCenter = useEditPrivacyCenter();
  const orgId = editPrivacyCenter?.props?.org?.id;
  const privacyCenterId = editPrivacyCenter?.props?.privacyCenter?.id;

  const history = useHistory();

  const stateSections = Object.values(editPrivacyCenter?.props?.privacyCenter?.anchorsByState || {})
    .map((anchor) =>
      editPrivacyCenter?.props?.privacyPolicy?.renderedPolicySections?.find(
        (section) => section.anchor === anchor,
      ),
    )
    .filter(Boolean);

  const [draftNotice, draftNoticeRequest, refreshDraftNotice] = usePrivacyCenterNotice(
    orgId,
    privacyCenterId,
    editPrivacyCenter?.props?.draftPrivacyPolicyNoticeId,
  );

  const isAlreadyPublished = editPrivacyCenter?.props?.allNotices?.some(
    (notice) => notice.type === "PRIVACY_POLICY" && notice.status === "PUBLISHED",
  );

  const dataRetentionModule = useDataRetentionModule();

  const { answers: incentivesAnswers } = useSurvey(orgId, CONSUMER_INCENTIVES_SURVEY_NAME);
  const offersFinancialIncentives = incentivesAnswers?.[OFFERS_CONSUMER_INCENTIVES_SLUG] === "true";
  const { answers: developerInstructionsAnswers } = useSurvey(
    orgId,
    DEVELOPER_INSTRUCTIONS_SURVEY_NAME,
  );

  const [currentStep, setCurrentStep] = useState<StateNoticesStep>("statesNoticesEditor");

  const openPreviewNotice = usePrivacyCenterPreviewPopup();

  const { updateNotice } = useUpdateNoticeApi();
  const enqueueSnackbar = useSnackBar();
  const sendPublish = async () => {
    try {
      await updateNotice({
        organizationId: orgId,
        privacyCenterId: draftNotice.privacyCenterId,
        noticeId: draftNotice.publicId,
        updateNoticeBody: {
          status: "PUBLISHED",
        },
      });

      await refreshDraftNotice();
      // refresh privacy center since privacyCenter.hiddenStateAnchors may have been invalidated
      await editPrivacyCenter?.props?.privacyCenterRefresh();
    } catch (error) {
      console.error("Failed to publish the notice:", error);
      enqueueSnackbar("Failed to publish. Please try again.", { variant: "error" });
    }
  };
  const [showPublishDateModal, setShowPublishDateModal] = useState(false);
  const { isDateSaving, onDateChange } = usePrivacyCenterPublish(
    orgId,
    draftNotice,
    refreshDraftNotice,
  );

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);

  const publishedAtNormalizedStr = draftNotice?.publishedAt?.replace(/-/g, "/");
  const publishedAtDate = moment(publishedAtNormalizedStr || new Date()).toDate();

  if (!draftNoticeRequest.success || !draftNotice || !orgId) {
    return <PageLoading />;
  }

  if (!stateSections || stateSections.length === 0) {
    return <Redirect to={Routes.privacyCenter.privacyPolicy} />;
  }

  const financialIncentiveSection = draftNotice.sections.find(
    (s) => s.anchor === "notice-of-financial-incentive",
  );
  const customRetentionPolicySection = draftNotice.sections.find(
    (s) => s.anchor === "data-retention-policy",
  );
  const biometricIdentifiersSection = draftNotice.sections.find(
    (s) => s.anchor === "colorado-biometric-identifiers-policy",
  );

  const isFinancialIncentiveSectionFinished =
    financialIncentiveSection?.content.length > 2 || isAlreadyPublished;
  const isRetentionPolicySectionFinished =
    (draftNotice.useCustomRetentionPolicySection
      ? customRetentionPolicySection?.content.length > 2
      : dataRetentionModule.isFinished) || isAlreadyPublished;
  const isBiometricIdentifiersSectionFinished = biometricIdentifiersSection?.content.length > 2;

  const collectsConsumerBiometricData =
    dataRetentionModule?.props?.collectedPic?.some((pic) => pic.picGroupKey === "biometric-info") ??
    false;

  // Show the first time user experience (e.g. footer actions) if either (1) there isn't a published notice yet OR (2) the Policies & Notices onboarding steps are not completed
  const isFirstTimeOnboarding = !(
    isAlreadyPublished && didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
  );

  const { nextContent, nextOnClick, nextDisabled, showFooter } = (() => {
    if (!isFirstTimeOnboarding) {
      return {
        nextContent: null,
        nextOnClick: null,
        nextDisabled: false,
        showFooter: false,
      };
    }

    if (currentStep === "statesNoticesEditor") {
      return {
        nextContent: "Done with US Notices",
        nextOnClick: () => history.push(Routes.privacyCenter.theme),
        nextDisabled: false,
        showFooter: true,
      };
    }

    if (currentStep === "financialIncentivesEditor") {
      return {
        nextContent: "Done with Financial Notice",
        nextOnClick: () => {
          setCurrentStep("statesNoticesEditor");
          // When going back to the main US State Notices page, scroll to the top of the page
          setTimeout(() => {
            document.querySelector("div.page-content").scrollTop = 0;
          }, 300);
        },
        nextDisabled: !isFinancialIncentiveSectionFinished,
        showFooter: true,
      };
    }

    if (currentStep === "biometricIdentifiersEditor") {
      return {
        nextContent: "Done with Biometric Policy",
        nextOnClick: () => {
          setCurrentStep("statesNoticesEditor");
          // When going back to the main US State Notices page, scroll to the top of the page
          setTimeout(() => {
            document.querySelector("div.page-content").scrollTop = 0;
          }, 300);
        },
        nextDisabled: !isBiometricIdentifiersSectionFinished,
        showFooter: true,
      };
    }

    return {
      nextContent: null,
      nextOnClick: null,
      nextDisabled: false,
      showFooter: false,
    };
  })();

  return (
    <StandardPageWrapper
      fullWidth
      footer={
        showFooter ? (
          <ProgressFooter
            actions={
              <ProgressFooterActions
                nextContent={nextContent}
                nextOnClick={nextOnClick}
                nextDisabled={nextDisabled}
              />
            }
          />
        ) : null
      }
    >
      <div>
        <div className="tw-boundary header-sticky">
          <Breadcrumbs title="Policies & Notices" sourceRoute={Routes.privacyCenter.root} />

          <PrivacyCenterTabsNew
            org={editPrivacyCenter?.props?.org}
            selectedTabKey="us-state-notices"
            enableNextTab={!nextDisabled}
            enableAllTabs={didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)}
            trailingContent={
              (currentStep === "statesNoticesEditor" ||
                currentStep === "financialIncentivesEditor" ||
                currentStep === "biometricIdentifiersEditor") && (
                <div className="flex items-center gap-2">
                  <div className="flex text-neutral-600 font-normal text-left">
                    <ProgressFooterSaveNotification saving={isSaving} isSaved={isSaved} />
                  </div>
                  <PrintPreviewPublishButtons
                    orgId={orgId}
                    privacyCenter={editPrivacyCenter?.props?.privacyCenter}
                    draftNoticePublicId={draftNotice.publicId}
                    hasPublishedNotice={
                      editPrivacyCenter?.props?.latestPublishedNoticeUpdatedOn != null
                    }
                    onPreviewClick={openPreviewNotice}
                    onPublishClick={() => setShowPublishDateModal(true)}
                    // In the onboarding/FTUE flow, we only enable the Publish button on the last step of the flow, so it should be disabled on this page if we're in FTUE
                    isPublishEnabled={
                      draftNotice?.sections.some(
                        (section) => !!section.header || !!section.content,
                      ) && didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
                    }
                    publishDisabledTooltip={
                      !didFinishPoliciesAndNoticesOnboarding(developerInstructionsAnswers)
                        ? "Complete the Themes step to publish your Privacy Center."
                        : undefined
                    }
                  />
                </div>
              )
            }
          />
        </div>

        {(currentStep === "statesNoticesEditor" ||
          currentStep === "financialIncentivesEditor" ||
          currentStep === "biometricIdentifiersEditor") && (
          <USStateNoticesEditor
            orgId={orgId}
            privacyCenterId={privacyCenterId}
            draftNotice={draftNotice}
            stateSections={stateSections}
            refreshNotice={refreshDraftNotice}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            offersFinancialIncentives={offersFinancialIncentives}
            isFinancialIncentiveSectionFinished={isFinancialIncentiveSectionFinished}
            isRetentionPolicySectionFinished={isRetentionPolicySectionFinished}
            collectsConsumerBiometricData={collectsConsumerBiometricData}
            isBiometricIdentifiersSectionFinished={isBiometricIdentifiersSectionFinished}
            setIsSaving={setIsSaving}
            setIsSaved={setIsSaved}
          />
        )}

        {showPublishDateModal && (
          <PrivacyCenterPublishDatePickerModal
            onPublishClick={sendPublish}
            closeModal={() => setShowPublishDateModal(false)}
            publishedAtDate={publishedAtDate}
            onDateChange={onDateChange}
            disabled={isDateSaving}
          />
        )}
      </div>
    </StandardPageWrapper>
  );
}

export function PrintPreviewPublishButtons({
  orgId,
  privacyCenter,
  draftNoticePublicId,
  hasPublishedNotice,
  onPreviewClick,
  onPublishClick,
  showPdfPreviewButton = true,
  isPublishEnabled = true,
  isPreviewEnabled = true,
  previewDisabledTooltip,
  previewButtonText,
  publishDisabledTooltip,
  isPublishing = false,
}: {
  orgId: string;
  privacyCenter: PrivacyCenterDto;
  draftNoticePublicId?: string;
  hasPublishedNotice: boolean;
  onPreviewClick?: () => void;
  onPublishClick?: () => void;
  showPdfPreviewButton?: boolean;
  isPublishEnabled?: boolean;
  isPreviewEnabled?: boolean;
  previewDisabledTooltip?: string;
  previewButtonText?: string;
  publishDisabledTooltip?: string;
  isPublishing?: boolean;
}) {
  const [hasSeenPreviewPopup, setHasSeenPreviewPopup] = useLocalStorage(
    "hasSeenPreviewPopup",
    "false",
  );
  const [showPreviewPopup, setShowPreviewPopup] = useState(false);

  return (
    <div className="flex items-center gap-sm">
      {showPdfPreviewButton && draftNoticePublicId && privacyCenter && (
        <>
          <a
            href={ApiRouteHelpers.organization.privacyCenter.notices.noticePdf(
              orgId,
              privacyCenter?.id,
              draftNoticePublicId,
            )}
            target="_blank"
            rel="noreferrer"
            title="Download PDF"
          >
            <div
              className="bg-primary-100 hover:bg-primary-200 rounded-full cursor-pointer"
              style={{ padding: "9px" }}
            >
              <PrinterIcon className="h-6 w-6 text-primary-600" />
            </div>
          </a>
          {/* Temporary: HTML download for testing */}
          <a
            href={ApiRouteHelpers.organization.privacyCenter.notices.noticeHtml(
              orgId,
              privacyCenter?.id,
              draftNoticePublicId,
            )}
            target="_blank"
            rel="noreferrer"
            title="Download HTML"
          >
            <div
              className="bg-green-100 hover:bg-green-200 rounded-full cursor-pointer"
              style={{ padding: "9px" }}
            >
              <DocumentTextIcon className="h-6 w-6 text-green-600" />
            </div>
          </a>
        </>
      )}
      {onPreviewClick && (
        <ToolTip text={!isPreviewEnabled && previewDisabledTooltip} width="250px">
          <Button
            color="secondary"
            onClick={() => {
              if (hasSeenPreviewPopup !== "true" && !hasPublishedNotice) {
                setShowPreviewPopup(true);
              } else {
                onPreviewClick();
              }
            }}
            disabled={!isPreviewEnabled}
          >
            <span className="flex items-center">
              <div style={{ marginRight: "8px" }}>
                <EyeIcon className="h-6 w-6 text-primary-600" />
              </div>
              {previewButtonText || "Preview"}
            </span>
          </Button>
        </ToolTip>
      )}
      {showPreviewPopup && (
        <Modal
          title={`Previewing your Privacy Center requires that we load it to your subdomain (${privacyCenter?.defaultSubdomain}.truevaultprivacycenter.com)`}
          alignText="left"
          firstButton="Back to Editing"
          firstButtonColor="tertiary"
          onFirstButtonClick={() => setShowPreviewPopup(false)}
          secondButton="Preview Privacy Center"
          secondButtonColor="primary"
          onSecondButtonClick={() => {
            setShowPreviewPopup(false);
            setHasSeenPreviewPopup("true");
            onPreviewClick();
          }}
          showOverlay
          onClose={() => setShowPreviewPopup(false)}
          alignButton="right"
        >
          <p className="text-left text-gray-500 text-[14px] font-normal">
            It will not be directly accessible to website visitors until you complete your Developer
            Instructions in a later step.
          </p>
        </Modal>
      )}
      {onPublishClick && (
        <ToolTip
          text={
            !isPublishEnabled
              ? publishDisabledTooltip || "Make a change, then click Publish"
              : undefined
          }
          width="250px"
        >
          <Button color="primary" onClick={onPublishClick} disabled={!isPublishEnabled}>
            <span className="flex items-center">
              <div style={{ marginRight: "8px" }}>
                {isPublishing ? (
                  <ArrowPathIcon className="h-6 w-6 animate-spin" />
                ) : (
                  <RocketLaunchIcon className="h-6 w-6" />
                )}
              </div>
              {isPublishing ? "Publishing..." : "Publish"}
            </span>
          </Button>
        </ToolTip>
      )}
    </div>
  );
}
